import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from '../settings/variables/Spacers';
import { COLORS } from '../settings/variables/Colors';
import { numberWithCommas, replaceCommas } from 'shared/utils/numberUtils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface TrueImpactDollarFieldProps {
  id?: string;
  value: string | number | undefined;
  defaultValue?: string | number;
  error?: boolean;
  disabled?: boolean;
  inputPaddingLeft?: string;
  iconTopPosition?: number;
  iconLeftPosition?: number;
  placeholder: string;
  className?: string;
  autoFocus?: boolean;
  icon?: IconProp;
  hideIcon?: boolean;
  toFixedDecimal?: number;
  inputRef?: React.RefObject<HTMLInputElement>;
  onBlur?: () => unknown;
  onChange?: (value: string) => unknown;
}

const StyledDiv = styled.div<{
  error?: boolean;
  hideIcon?: boolean;
  inputPaddingLeft?: string;
  iconTopPosition?: number;
  iconLeftPosition?: number;
}>`
  cursor: text;
  position: relative;
  .icon {
    position: absolute;
    top: ${({ iconTopPosition }) => iconTopPosition}px;
    left: ${({ iconLeftPosition }) => iconLeftPosition}px;
    path {
      fill: ${COLORS.ctaPrimary};
    }
  }
  input {
    width: 100%;
    border-radius: 4px;
    border: 1px solid
      ${({ error }) => (error ? COLORS.critical : COLORS.grayBase)};
    padding: 14px ${SPACERS.small} 14px
      ${({ hideIcon, inputPaddingLeft }) =>
        hideIcon ? SPACERS.small : inputPaddingLeft || SPACERS.extraLarge};
    &:disabled {
      background-color: ${COLORS.grayLight};
    }
  }
`;

export const dollarAmountDefaultValue = '0';
export const dollarAmountTwoDecimalDefaultValue = '0.00';
export const dollarToInt = (dollar?: string): number =>
  parseFloat(
    dollar?.toString().replace(/,/g, '') || dollarAmountDefaultValue,
  ) || 0;
export const numberIsGreaterThanZero = (num: number | string): boolean =>
  Number(num?.toString().replace(/,/g, '')) > 0;

export const removeDollarSign = (str: string | number | undefined): string => {
  if (!str) {
    return '';
  } else if (typeof str === 'number') {
    return str.toString();
  } else if (str.startsWith('$$')) {
    return str.slice(2);
  } else if (str.charAt(0) === '$') {
    return str.slice(1);
  }
  return str;
};

const TrueImpactDollarField: React.FC<TrueImpactDollarFieldProps> = ({
  id,
  error,
  disabled,
  value,
  inputPaddingLeft,
  iconTopPosition = 15,
  iconLeftPosition = 16,
  defaultValue,
  inputRef,
  placeholder,
  className,
  autoFocus,
  icon = faDollarSign,
  hideIcon,
  toFixedDecimal,
  onBlur,
  onChange,
}) => {
  const ref = inputRef || React.useRef<HTMLInputElement>(null);
  const [cursor, setCursor] = useState<number | null>(null);

  useEffect(() => {
    ref.current?.setSelectionRange(cursor, cursor);
  }, [ref.current, cursor, value]);

  const handleClickAnywhere = useCallback(() => {
    ref.current?.focus();
  }, [ref.current]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { selectionStart } = e.target;
      const value = removeDollarSign(e.target.value);
      const lastDigit = value[value.length - 1];
      if (
        lastDigit !== '0' &&
        lastDigit !== '.' &&
        value !== '' &&
        !Number(lastDigit)
      )
        return;
      const newValue = numberWithCommas(replaceCommas(value));
      onChange?.(newValue);
      setCursor(
        value.length === newValue.length - 1
          ? (selectionStart || 0) + 1
          : selectionStart,
      );
    },
    [onChange, setCursor],
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    if (ref.current && toFixedDecimal !== undefined) {
      const value = ref.current.value;
      if (!value) return;
      const num = parseFloat(replaceCommas(value));
      if (!num) {
        onChange?.('');
        ref.current.value = '';
        return;
      }
      const newValue = numberWithCommas(num.toFixed(toFixedDecimal) || '');
      ref.current.value = newValue;
      onChange?.(newValue);
    }
  }, [onBlur, onChange, ref.current, toFixedDecimal]);

  return (
    <StyledDiv
      error={error}
      hideIcon={hideIcon}
      className={className}
      iconTopPosition={iconTopPosition}
      iconLeftPosition={iconLeftPosition}
      inputPaddingLeft={inputPaddingLeft}
      onClick={handleClickAnywhere}
    >
      {!hideIcon && <FontAwesomeIcon icon={icon} className="icon" />}
      <input
        id={id}
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        autoFocus={autoFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder={placeholder}
      />
    </StyledDiv>
  );
};

export default TrueImpactDollarField;
